/**
 * Render a requester / commenter's first name from whatever the backend
 * stored. Three cases this has to survive:
 *
 *   1. Already-stored full name like "Daniel Lima" — return "Daniel".
 *   2. Legacy rows where the backend stored the email by mistake (the
 *      pre-fix bug in resolveRequester selected firstName+lastName fields
 *      that didn't exist on User, so requesterName fell back to email
 *      across the board) — pull the local part, normalize, capitalize.
 *   3. Empty / null — return an empty string so the caller can fall back.
 *
 * Keep this pure + dependency-free so it can be inlined in list rows and
 * detail headers without a render-time cost.
 */
export function firstNameOf(raw: string | null | undefined): string {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (!trimmed) return '';
  // Email-shaped → use the local part as the seed.
  const atIndex = trimmed.indexOf('@');
  if (atIndex > 0 && /\s/.test(trimmed) === false) {
    const local = trimmed.slice(0, atIndex);
    const seed = local.split(/[.\-_+]/)[0] ?? local;
    return capitalize(seed);
  }
  // Otherwise take the first whitespace-separated token.
  return trimmed.split(/\s+/)[0] ?? trimmed;
}

function capitalize(s: string): string {
  if (!s) return '';
  return s[0]!.toUpperCase() + s.slice(1).toLowerCase();
}
